import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';

import { confirmCode, requestSmsCode } from '../../api/authService';
import resetStore from '../resetStore';
import { setUser } from './userSlice';

const initialState = {
  error: null,
  fetchStatus: 'idle',
  isCodeSent: false,
  isAuthenticated: false,
  id: null,
  accessToken: '',
};

const translateAuthError = (errorCode) => {
  switch (errorCode) {
    case 'invalid_credentials':
      return 'Неверный код. Проверьте СМС и попробуйте снова.';
    default:
      return 'Произошла ошибка. Пожалуйста, попробуйте ещё раз.';
  }
};

export const sendSmsCode = createAsyncThunk(
  'auth/sendSmsCode',
  async (phone, { rejectWithValue }) => {
    try {
      const res = await requestSmsCode(phone);
      if (!res.id) {
        return rejectWithValue('Неверный формат ответа от сервера');
      }
      return res;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      return rejectWithValue(message || 'Не удалось отправить СМС. Попробуйте позже.');
    }
  }
);

export const verifySmsCode = createAsyncThunk(
  'auth/verifySmsCode',
  async ({ id, code }, { rejectWithValue, dispatch }) => {
    try {
      const res = await confirmCode(id, code);
      // Раньше успех определялся по наличию res.access_token, и тогда же
      // писалась кука на фронте (Cookies.set('auth_token', ...)). Сервер
      // теперь сам ставит httpOnly-куку через Set-Cookie - фронту сюда
      // лезть незачем, значение токена ему больше не нужно и не видно.
      // Раз access_token готовится к удалению из тела ответа, проверяем
      // успех через ОТСУТСТВИЕ res.error - тот же принцип, что уже
      // используется в перехватчике api.js для access_denied (ошибка
      // приходит в теле при статусе 200, а не через HTTP-код).
      if (res.error) {
        const translatedError = translateAuthError(res.error);
        return rejectWithValue(translatedError);
      }
      // init_auth_token на домене ФРОНТЕНДА (не httpOnly) пишется только
      // InitPage.jsx для служебной ссылки входа, срок жизни 7 дней.
      // Ничто не стирало эту куку раньше - если сотрудник когда-то
      // заходил по такой ссылке, а теперь входит обычным способом по
      // SMS, устаревшее значение оставалось лежать в браузере до 7 дней
      // и перехватчик в api.js безусловно подставлял его в заголовок
      // Authorization - перекрывая свежую httpOnly-сессию, даже если
      // сервер её только что выдал. Явно стираем на успешном обычном
      // входе, чтобы свежая сессия ничем не перекрывалась.
      Cookies.remove('init_auth_token');
      dispatch(setUser(res.user));
      return res;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      return rejectWithValue(
        message || 'Не удалось подтвердить код. Пожалуйста, попробуйте позже.'
      );
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  extraReducers: (builder) => {
    // requestSmsCode
    builder
      .addCase(sendSmsCode.pending, (state) => {
        state.fetchStatus = 'loading';
        state.error = null;
      })
      .addCase(sendSmsCode.fulfilled, (state, action) => {
        state.fetchStatus = 'succeeded';
        state.isCodeSent = true;
        state.id = action.payload.id;
      })
      .addCase(sendSmsCode.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.error = action.payload;
      });
    // confirmCode
    builder
      .addCase(verifySmsCode.pending, (state) => {
        state.fetchStatus = 'loading';
        state.error = null;
      })
      .addCase(verifySmsCode.fulfilled, (state) => {
        state.fetchStatus = 'succeeded';
        state.isAuthenticated = true;
      })
      .addCase(verifySmsCode.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.error = action.payload;
      });
    builder.addCase(resetStore, () => initialState);
  },
});

export default authSlice.reducer;
